import React, { FC, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Buffer } from 'buffer'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PublicKey, SystemProgram } from '@solana/web3.js'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import {
  Video,
  AddendumTrack,
  ChainAnchor,
  ChainStatePublished,
  ChainStateSealed,
  Container,
  ContainerAnchor,
  VersionRefAnchor,
} from '../../types'
import { resolveContainerForVideo, type ContainerResolution } from '../../system/apitools'
import {
  FORUM_IDL,
  FORUM_PROGRAM_ID,
  useForumVideo,
  useForumContainer,
  videoIdBytes,
  rootHashBytes,
  langBytes,
  hashAlgoCode,
  humanizeForumError,
  subtitlePda,
  storagePda,
  versionRefPda,
  addSubtitleTrack,
  addStorageLocation,
  initContainer,
  appendVersion,
  DRIVER_HTTPS,
  type PublisherAccount,
  type VideoAccount,
} from '../../solana/forum'
import {
  SolanaProvider,
  ConnectWallet,
  ClusterBadge,
  useProgram,
  useProgramReader,
  useSendTransaction,
  useWalletAddress,
  humanizeError,
} from '../../solana/react'
import { explorerUrl } from '../../solana/kit'
import { solanaCluster, clusterRpcUrl, clusterWsUrl } from '../../solana/endpoints'
// Our copy of the adapter's stylesheet, minus its Google Fonts @import.
import './walletAdapterStyles.css'

// The site's own name, used as a publisher's on-chain display name. Nobody is
// prompted for one — the program only needs *something* short and legible,
// not a real identity, so asking a publisher to type a name here would be
// friction for no benefit.
const SITE_DISPLAY_NAME = 'The Forum'

const formatChainDate = (seconds: number): string => {
  if (seconds <= 0) return 'unknown time'
  return new Date(seconds * 1000).toLocaleString()
}

// Addendum dates come off AddendumAnchor.added_at, which is already an ISO
// string by the time it reaches the browser (the chain's i64 seconds,
// converted once when it was read back off SubtitleTrack — see
// recordAddendumAnchor). formatChainDate takes raw epoch seconds instead, so
// this is a second tiny helper rather than a second calling convention for
// the same one.
const formatIsoDate = (iso: string): string => new Date(iso).toLocaleString()

const toHex = (bytes: number[] | Uint8Array): string => Buffer.from(bytes).toString('hex')

// Anchor decodes the Rust enum's variants as `{ published: {} }` / `{ sealed: {} }`.
// The program has exactly these two, so "not sealed" is always "published".
const chainStateOf = (account: VideoAccount): string =>
  'sealed' in (account.state as object) ? ChainStateSealed : ChainStatePublished

/**
 * Builds the POST body for /videos/:id/anchor from a *fresh read* of the
 * on-chain Video account — never from the arguments a transaction sent, per
 * the trust boundary documented on server.AnchorVideo: this is a record of
 * what actually landed, and a stale local guess would defeat the point.
 */
const buildChainAnchor = (
  account: VideoAccount,
  ctx: { publisherPda: PublicKey; videoPda: PublicKey; authority: PublicKey; signature: string },
): ChainAnchor => {
  const sealedAtSeconds = account.sealedAt.toNumber()
  return {
    cluster: solanaCluster,
    program_id: FORUM_PROGRAM_ID.toBase58(),
    publisher_pda: ctx.publisherPda.toBase58(),
    video_pda: ctx.videoPda.toBase58(),
    authority: ctx.authority.toBase58(),
    signature: ctx.signature,
    revision: account.revision,
    root_hash: toHex(account.rootHash),
    state: chainStateOf(account),
    published_at: new Date(account.publishedAt.toNumber() * 1000).toISOString(),
    // A zero sealedAt means "never sealed" on chain. Go's time.Time zero
    // value serialises as "0001-01-01T00:00:00Z", not the Unix epoch — the
    // generated ChainAnchor type keeps sealed_at non-optional, so rather than
    // omit the field (which the type would reject) this sends Go's own zero
    // time string, which round-trips as "the zero value" on the other end.
    sealed_at: sealedAtSeconds > 0 ? new Date(sealedAtSeconds * 1000).toISOString() : '0001-01-01T00:00:00Z',
    recorded_at: new Date().toISOString(),
  }
}

interface ChainAnchorInnerProps {
  video: Video
}

const ChainAnchorInner: FC<ChainAnchorInnerProps> = ({ video }) => {
  const queryClient = useQueryClient()
  const address = useWalletAddress()
  const authority = useMemo(() => (address ? new PublicKey(address) : null), [address])

  // Two handles, same discipline used throughout frontend/src/solana: `reader`
  // needs no wallet and does every read (including the post-transaction
  // re-read below); `program` exists only to build instructions for the
  // connected wallet to sign.
  const reader = useProgramReader(FORUM_IDL, FORUM_PROGRAM_ID)
  const program = useProgram(FORUM_IDL, FORUM_PROGRAM_ID)
  const { send, pending } = useSendTransaction()

  const {
    publisherPda,
    videoPda,
    video: chainVideo,
    missing: videoMissing,
    loading: videoLoading,
  } = useForumVideo(video.id, address)

  // The container this video is one version of. Keyed exactly as the admin
  // page keys it (pages/Video.tsx), so every anchor recorded below reaches
  // ContainerSection and VersionsSection without a second request. Resolves to
  // null — not an error — for a video that has never been published and so has
  // no container yet.
  const { data: containerResolution, isPending: containerPending } = useQuery({
    queryKey: ['container-for-video', video.id],
    queryFn: () => resolveContainerForVideo(video.id),
  })
  const container = containerResolution?.container ?? null

  // What the CHAIN says about that container, read the same wallet-free way
  // the Video account is read above. Which button to offer must follow the
  // chain, never container.chain: that field is a cache of what some browser
  // once reported (see types.ContainerAnchor), and offering "create" against a
  // container that already exists only produces a transaction that fails at
  // init. Passing '' while the container is still loading derives no PDA and
  // reads nothing — useForumContainer degrades instead of throwing.
  const {
    containerPda,
    container: chainContainer,
    versions: chainVersions,
    loading: containerChainLoading,
    missing: containerChainMissing,
    error: containerChainError,
    refresh: refreshContainerChain,
  } = useForumContainer(container?.id ?? '', address)

  // This video's own entry in the container's off-chain version list. A draft
  // — the next version being prepared — is not in it yet, and has nothing to
  // append.
  const containerEntry = container?.versions.find((v) => v.video_id === video.id)

  // Whether the chain's version list already names this video, compared on the
  // 16 raw id bytes the VersionRef actually stores rather than on an index,
  // so a list that has drifted from ours cannot read as "already appended".
  const videoIdHex = useMemo(() => {
    try {
      return toHex(videoIdBytes(video.id))
    } catch {
      return null
    }
  }, [video.id])
  const versionOnChain = videoIdHex !== null && chainVersions.some((v) => toHex(v.ref.videoId) === videoIdHex)

  // useForumVideo covers the Video account; the Publisher account is read
  // separately here, exactly as instructed: fetchNullable off the read-only
  // program handle, where null unambiguously means "never created" rather
  // than "not loaded yet" (tracked by publisherChecked).
  const [publisher, setPublisher] = useState<PublisherAccount | null>(null)
  const [publisherChecked, setPublisherChecked] = useState(false)
  const [publisherGeneration, setPublisherGeneration] = useState(0)

  useEffect(() => {
    if (!publisherPda) {
      setPublisher(null)
      setPublisherChecked(false)
      return
    }
    let live = true
    reader.account.publisher
      .fetchNullable(publisherPda)
      .then((acc) => {
        if (!live) return
        setPublisher(acc)
        setPublisherChecked(true)
      })
      .catch(() => {
        if (!live) return
        setPublisher(null)
        setPublisherChecked(true)
      })
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reader is a
    // fresh object every render; keyed on the address string and the
    // explicit generation bump instead, same discipline as useForumVideo.
  }, [publisherPda?.toBase58(), publisherGeneration])

  const [actionError, setActionError] = useState<string | null>(null)
  const [postError, setPostError] = useState<string | null>(null)
  const [pendingSignature, setPendingSignature] = useState<string | null>(null)
  const [posting, setPosting] = useState(false)
  const [confirmSealOpen, setConfirmSealOpen] = useState(false)

  const info = video.publication

  // Step 2 of "after every successful transaction": re-read the account
  // (never trust what was sent), POST it, and update the query cache the way
  // PublicationSection's own mutations do. Kept separate from the send() call
  // so a failed POST can be retried on its own — see retryPost — without
  // ever re-signing, which is the one mistake that would double-anchor.
  const recordAnchor = async (signature: string) => {
    if (!videoPda || !publisherPda || !authority) return
    setPosting(true)
    setPostError(null)
    try {
      const fresh = await reader.account.video.fetch(videoPda)
      const body = buildChainAnchor(fresh, { publisherPda, videoPda, authority, signature })
      const res = await axios.post<Video>(`/api/v1/videos/${video.id}/anchor`, body)
      queryClient.setQueryData(['video', video.id], res.data)
      queryClient.invalidateQueries({ queryKey: ['video', video.id] })
      setPendingSignature(null)
    } catch (err) {
      // The transaction already landed — the chain has it, our record does
      // not. Say so plainly, and keep only the signature (not the built
      // body) so Retry re-reads the account fresh rather than replaying a
      // possibly-stale guess, and above all never sends another transaction.
      setPendingSignature(signature)
      const detail = (err as any)?.response?.data?.error || (err as Error).message
      setPostError(
        `The transaction landed on chain (signature ${signature}) but recording it here failed: ` +
          `${detail}. The chain has this anchor; our database does not yet. Retry re-reads the ` +
          'account and tries the recording again — it will not ask you to sign anything.',
      )
    } finally {
      setPosting(false)
    }
  }

  const retryPost = () => {
    if (pendingSignature) recordAnchor(pendingSignature)
  }

  const busy = pending || posting

  // Addendum anchoring (T9). Keyed per language rather than a single flag
  // like the fields above, because more than one addendum can sit unanchored
  // on the same sealed video at once, and each must be retryable on its own
  // without disturbing the others.
  const [addendaBusy, setAddendaBusy] = useState<Record<string, boolean>>({})
  const [addendaActionError, setAddendaActionError] = useState<Record<string, string | null>>({})
  const [addendaPendingSignature, setAddendaPendingSignature] = useState<Record<string, string | null>>({})
  const [addendaPostError, setAddendaPostError] = useState<Record<string, string | null>>({})

  // Mirrors recordAnchor above: re-read the account rather than trust the
  // transaction args, POST it, update the query cache, and on failure keep
  // only the signature so Retry re-reads and re-posts without ever signing
  // again. The one addition is reading SubtitleTrack.added_at back off the
  // account just anchored — the chain's clock, not this browser's — for the
  // same reason recordAnchor reads publishedAt off the Video account instead
  // of stamping its own Date.now().
  const recordAddendumAnchor = async (lang: string, signature: string) => {
    if (!videoPda) return
    setAddendaBusy((s) => ({ ...s, [lang]: true }))
    setAddendaPostError((s) => ({ ...s, [lang]: null }))
    try {
      const pda = subtitlePda(videoPda, langBytes(lang))
      const account = await reader.account.subtitleTrack.fetch(pda)
      const body = {
        kind: 'subtitle',
        lang,
        subtitle_pda: pda.toBase58(),
        signature,
        added_at: new Date(account.addedAt.toNumber() * 1000).toISOString(),
      }
      const res = await axios.post<Video>(`/api/v1/videos/${video.id}/anchor-append`, body)
      queryClient.setQueryData(['video', video.id], res.data)
      queryClient.invalidateQueries({ queryKey: ['video', video.id] })
      setAddendaPendingSignature((s) => ({ ...s, [lang]: null }))
    } catch (err) {
      // Same shape as recordAnchor's failure banner: the transaction already
      // landed, only the recording of it failed, so Retry must re-read and
      // re-post, never re-sign.
      setAddendaPendingSignature((s) => ({ ...s, [lang]: signature }))
      const detail = (err as any)?.response?.data?.error || (err as Error).message
      setAddendaPostError((s) => ({
        ...s,
        [lang]:
          `The transaction landed on chain (signature ${signature}) but recording it here failed: ` +
          `${detail}. The chain has this anchor; our database does not yet. Retry re-reads the ` +
          'account and tries the recording again — it will not ask you to sign anything.',
      }))
    } finally {
      setAddendaBusy((s) => ({ ...s, [lang]: false }))
    }
  }

  const retryAddendumPost = (lang: string) => {
    const signature = addendaPendingSignature[lang]
    if (signature) recordAddendumAnchor(lang, signature)
  }

  // Storage-location anchoring (T10). A single flag/error/signature, not a
  // Record keyed like the addenda state above: there is only ever one
  // "current" host to offer at a time (PublicationSection's
  // hostVerifiedNotRecorded, driven off info.host_verified_url), never a
  // list. storagePendingIndex rides along with storagePendingSignature
  // because retrying the POST needs the same index the landed transaction
  // used — recomputing it from a fresh storage_count read here would name a
  // different (not-yet-existing) slot than the one the signature actually
  // claimed.
  const [storageBusy, setStorageBusy] = useState(false)
  const [storageActionError, setStorageActionError] = useState<string | null>(null)
  const [storagePendingSignature, setStoragePendingSignature] = useState<string | null>(null)
  const [storagePendingIndex, setStoragePendingIndex] = useState<number | null>(null)
  const [storagePostError, setStoragePostError] = useState<string | null>(null)

  // Mirrors recordAddendumAnchor: re-read the just-written account rather
  // than trust what was sent (driver/location come off StorageLocation, not
  // off info.host_verified_url or the DRIVER_HTTPS constant), POST it,
  // update the query cache, and on failure keep only signature+index so
  // Retry re-reads and re-posts without ever signing again. added_at is the
  // chain's clock, off StorageLocation.added_at, same discipline as
  // SubtitleTrack.added_at above.
  const recordStorageAnchor = async (index: number, signature: string) => {
    if (!videoPda) return
    setStorageBusy(true)
    setStoragePostError(null)
    try {
      const pda = storagePda(videoPda, index)
      const account = await reader.account.storageLocation.fetch(pda)
      const body = {
        kind: 'storage',
        index,
        driver: account.driver,
        location: account.location,
        storage_pda: pda.toBase58(),
        signature,
        added_at: new Date(account.addedAt.toNumber() * 1000).toISOString(),
      }
      const res = await axios.post<Video>(`/api/v1/videos/${video.id}/anchor-append`, body)
      queryClient.setQueryData(['video', video.id], res.data)
      queryClient.invalidateQueries({ queryKey: ['video', video.id] })
      setStoragePendingSignature(null)
      setStoragePendingIndex(null)
    } catch (err) {
      setStoragePendingSignature(signature)
      setStoragePendingIndex(index)
      const detail = (err as any)?.response?.data?.error || (err as Error).message
      setStoragePostError(
        `The transaction landed on chain (signature ${signature}) but recording it here failed: ` +
          `${detail}. The chain has this anchor; our database does not yet. Retry re-reads the ` +
          'account and tries the recording again — it will not ask you to sign anything.',
      )
    } finally {
      setStorageBusy(false)
    }
  }

  const retryStoragePost = () => {
    if (storagePendingSignature !== null && storagePendingIndex !== null) {
      recordStorageAnchor(storagePendingIndex, storagePendingSignature)
    }
  }

  // Container and version anchoring (T17). Two flags rather than one: the
  // container is created once and a version is appended once per version, and
  // the second is offered the moment the first lands, so a single busy flag
  // would disable a button that is now the only thing left to do.
  const [containerBusy, setContainerBusy] = useState(false)
  const [containerActionError, setContainerActionError] = useState<string | null>(null)
  const [containerPendingSignature, setContainerPendingSignature] = useState<string | null>(null)
  const [containerPostError, setContainerPostError] = useState<string | null>(null)

  const [versionBusy, setVersionBusy] = useState(false)
  const [versionActionError, setVersionActionError] = useState<string | null>(null)
  const [versionPendingSignature, setVersionPendingSignature] = useState<string | null>(null)
  const [versionPostError, setVersionPostError] = useState<string | null>(null)

  // Both container POSTs answer with the whole updated container, so this is
  // the same cache update recordAnchor does for the video, against the key
  // pages/Video.tsx reads.
  const applyContainer = (updated: Container) => {
    queryClient.setQueryData(['container-for-video', video.id], (old: ContainerResolution | null | undefined) =>
      old ? { ...old, container: updated } : old,
    )
    queryClient.invalidateQueries({ queryKey: ['container-for-video', video.id] })
  }

  // Same shape as recordAnchor: re-read the account that was just written and
  // build the body from THAT, never from what the transaction sent. created_at
  // is the chain's clock off VideoContainer.created_at, and version_count is
  // what the account actually holds — recorded_at is the only timestamp this
  // browser is entitled to invent, because it means "when we read it back".
  const recordContainerAnchor = async (signature: string) => {
    if (!containerPda || !publisherPda || !authority || !container) return
    setContainerBusy(true)
    setContainerPostError(null)
    try {
      const fresh = await reader.account.videoContainer.fetch(containerPda)
      const body: ContainerAnchor = {
        cluster: solanaCluster,
        program_id: FORUM_PROGRAM_ID.toBase58(),
        publisher_pda: publisherPda.toBase58(),
        container_pda: containerPda.toBase58(),
        authority: authority.toBase58(),
        signature,
        version_count: fresh.versionCount,
        created_at: new Date(fresh.createdAt.toNumber() * 1000).toISOString(),
        recorded_at: new Date().toISOString(),
      }
      const res = await axios.post<Container>(`/api/v1/containers/${container.id}/anchor`, body)
      applyContainer(res.data)
      setContainerPendingSignature(null)
    } catch (err) {
      // The transaction already landed. Keep only the signature — never the
      // body — so Retry re-reads the account and re-posts, and never asks for
      // another signature.
      setContainerPendingSignature(signature)
      const detail = (err as any)?.response?.data?.error || (err as Error).message
      setContainerPostError(
        `The transaction landed on chain (signature ${signature}) but recording it here failed: ` +
          `${detail}. The chain has this container; our database does not yet. Retry re-reads the ` +
          'account and tries the recording again — it will not ask you to sign anything.',
      )
    } finally {
      setContainerBusy(false)
    }
  }

  const retryContainerPost = () => {
    if (containerPendingSignature) recordContainerAnchor(containerPendingSignature)
  }

  // The VersionRef just written sits at version_count - 1, so its address is
  // derived from a fresh read of the container rather than from the index this
  // browser believed before signing — appendVersion never returns one, exactly
  // so that a stale index cannot be threaded through. index and added_at then
  // come off the VersionRef account itself. The video-id check is what stops a
  // race (someone else appending between our transaction and this read) from
  // recording their version's ref against ours; it fails the POST rather than
  // writing the wrong thing, and Retry re-reads without re-signing.
  const recordVersionAnchor = async (signature: string) => {
    if (!containerPda || !container) return
    setVersionBusy(true)
    setVersionPostError(null)
    try {
      const freshContainer = await reader.account.videoContainer.fetch(containerPda)
      const index = freshContainer.versionCount - 1
      if (index < 0) throw new Error('the container on chain still reports no versions')
      const pda = versionRefPda(containerPda, index)
      const account = await reader.account.versionRef.fetch(pda)
      if (toHex(account.videoId) !== videoIdHex) {
        throw new Error(
          `the newest version on chain (index ${index}) is a different video — another version was ` +
            'appended in between. Reload this page and check the version list before trying again.',
        )
      }
      const body: VersionRefAnchor = {
        version_pda: pda.toBase58(),
        index: account.index,
        // The video the ref ACTUALLY names, not the one this page believes it
        // is anchoring — the check above has just proved they agree, so this
        // sends the value that was verified rather than re-deriving one. The
        // server refuses a report whose video_id does not match the entry at
        // the reported index, which turns any drift between the two lists into
        // a refusal instead of an anchor filed against the wrong version.
        video_id: video.id,
        signature,
        added_at: new Date(account.addedAt.toNumber() * 1000).toISOString(),
      }
      const res = await axios.post<Container>(`/api/v1/containers/${container.id}/anchor-version`, body)
      applyContainer(res.data)
      setVersionPendingSignature(null)
    } catch (err) {
      setVersionPendingSignature(signature)
      const detail = (err as any)?.response?.data?.error || (err as Error).message
      setVersionPostError(
        `The transaction landed on chain (signature ${signature}) but recording it here failed: ` +
          `${detail}. The chain has this version; our database does not yet. Retry re-reads the ` +
          'account and tries the recording again — it will not ask you to sign anything.',
      )
    } finally {
      setVersionBusy(false)
    }
  }

  const retryVersionPost = () => {
    if (versionPendingSignature) recordVersionAnchor(versionPendingSignature)
  }

  const handleInitContainer = async () => {
    if (!program || !container) return
    setContainerActionError(null)
    setContainerBusy(true)
    try {
      const signature = await initContainer(program, container.id)
      await recordContainerAnchor(signature)
    } catch (err) {
      setContainerActionError(humanizeForumError(err) ?? humanizeError(err))
    } finally {
      setContainerBusy(false)
      // Re-read the chain either way: on success the container now exists and
      // the append offer below depends on knowing that, and on failure this
      // costs one request to stop showing a button for work already done.
      refreshContainerChain()
    }
  }

  // No index is passed: appendVersion reads the container's version_count
  // fresh at the moment of signing, for the same reason handleRecordStorage
  // reads storage_count fresh — the VersionRef PDA is seeded on that number.
  const handleAppendVersion = async () => {
    if (!program || !container) return
    setVersionActionError(null)
    setVersionBusy(true)
    try {
      const signature = await appendVersion(program, container.id, video.id)
      await recordVersionAnchor(signature)
    } catch (err) {
      setVersionActionError(humanizeForumError(err) ?? humanizeError(err))
    } finally {
      setVersionBusy(false)
      refreshContainerChain()
    }
  }

  // THE correctness point carried forward from T8 (see append.ts's doc
  // comment on addStorageLocation): index must be the Video account's
  // storage_count read FRESH, right here, at the moment of signing — never
  // StorageAnchors.length, never a value cached from an earlier render. The
  // storage PDA is seeded on that number, so a stale index derives a PDA
  // that already exists and the transaction fails at init.
  const handleRecordStorage = async () => {
    if (!program || !videoPda || !info?.host_verified_url) return
    setStorageActionError(null)
    try {
      const fresh = await reader.account.video.fetch(videoPda)
      const index = fresh.storageCount
      const signature = await addStorageLocation({
        program,
        videoPda,
        index,
        driver: DRIVER_HTTPS,
        location: info.host_verified_url,
      })
      await recordStorageAnchor(index, signature)
    } catch (err) {
      setStorageActionError(humanizeForumError(err) ?? humanizeError(err))
    }
  }

  // D11: anchor the language string exactly as stored on the track
  // (track.lang), never a value re-derived from a form field, a picker label
  // or a URL segment. The chain's shadowing guard is a raw byte comparison
  // against sealed_langs, so a mis-cased code slips past it; the API already
  // normalises AddendumTrack.Lang to trimmed lower case on write, so passing
  // it through verbatim here is what keeps this correct.
  const handleAnchorAddendum = async (track: AddendumTrack) => {
    if (!program || !videoPda) return
    setAddendaActionError((s) => ({ ...s, [track.lang]: null }))
    try {
      const signature = await addSubtitleTrack({
        program,
        videoPda,
        lang: track.lang,
        hashHex: track.hash,
      })
      await recordAddendumAnchor(track.lang, signature)
    } catch (err) {
      setAddendaActionError((s) => ({ ...s, [track.lang]: humanizeForumError(err) ?? humanizeError(err) }))
    }
  }

  const handleCreatePublisher = async () => {
    if (!program || !publisherPda || !authority) return
    setActionError(null)
    try {
      const displayName = SITE_DISPLAY_NAME.slice(0, 64)
      const ix = await program.methods
        .initPublisher(displayName)
        .accountsPartial({ publisher: publisherPda, authority, systemProgram: SystemProgram.programId })
        .instruction()
      await send([ix])
      setPublisherGeneration((g) => g + 1)
    } catch (err) {
      setActionError(humanizeForumError(err) ?? humanizeError(err))
    }
  }

  const handlePublish = async () => {
    if (!program || !publisherPda || !videoPda || !authority || !info) return
    setActionError(null)
    try {
      const ix = await program.methods
        .publishVideo(
          // publishVideo's videoId arg is a fixed [u8; 16] and Anchor types
          // it as number[] — videoIdBytes returns a Buffer for PDA
          // derivation, so it needs converting here to satisfy that shape.
          Array.from(videoIdBytes(video.id)),
          rootHashBytes(info.root_hash),
          hashAlgoCode(info.hash_algo || 'sha256'),
          (info.sealed_langs ?? []).map(langBytes),
        )
        .accountsPartial({
          publisher: publisherPda,
          video: videoPda,
          authority,
          systemProgram: SystemProgram.programId,
        })
        .instruction()
      const sig = await send([ix])
      await recordAnchor(sig)
    } catch (err) {
      setActionError(humanizeForumError(err) ?? humanizeError(err))
    }
  }

  const handleRepublish = async () => {
    if (!program || !publisherPda || !videoPda || !authority || !info) return
    setActionError(null)
    try {
      const ix = await program.methods
        .republishVideo(
          rootHashBytes(info.root_hash),
          hashAlgoCode(info.hash_algo || 'sha256'),
          (info.sealed_langs ?? []).map(langBytes),
        )
        .accountsPartial({ publisher: publisherPda, video: videoPda, authority })
        .instruction()
      const sig = await send([ix])
      await recordAnchor(sig)
    } catch (err) {
      setActionError(humanizeForumError(err) ?? humanizeError(err))
    }
  }

  const handleSeal = async () => {
    if (!program || !publisherPda || !videoPda || !authority) return
    setActionError(null)
    try {
      const ix = await program.methods
        .sealVideo()
        .accountsPartial({ publisher: publisherPda, video: videoPda, authority })
        .instruction()
      const sig = await send([ix])
      await recordAnchor(sig)
    } catch (err) {
      setActionError(humanizeForumError(err) ?? humanizeError(err))
    }
  }

  // 1. No manifest, nothing to anchor. The outer component already skips
  // mounting the provider in this case; this guard just keeps the branches
  // below able to assume `info` exists.
  if (!info) return null

  const videoAddress = videoPda?.toBase58() ?? null
  const explorerHref = videoAddress ? explorerUrl(solanaCluster, 'address', videoAddress, clusterRpcUrl()) : null

  // Shown above every state below: a transaction that landed but never made
  // it into our own record must never be silently retried as if nothing
  // happened, and must never be "fixed" by sending a second transaction.
  const pendingPostBanner = pendingSignature && (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={retryPost} disabled={posting}>
          {posting ? 'Retrying...' : 'Retry'}
        </Button>
      }
    >
      {postError}
    </Alert>
  )

  const actionErrorBanner = actionError && (
    <Alert severity="error" sx={{ mb: 2 }}>
      {actionError}
    </Alert>
  )

  // The container/version equivalents of the banner above. Each carries its
  // own signature, error and Retry rather than sharing the video's: three
  // different transactions can be landed-but-unrecorded at once, and a single
  // Retry could only ever re-post one of them.
  const containerPendingBanner = containerPendingSignature && (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={retryContainerPost} disabled={containerBusy}>
          {containerBusy ? 'Retrying...' : 'Retry'}
        </Button>
      }
    >
      {containerPostError}
    </Alert>
  )

  const versionPendingBanner = versionPendingSignature && (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={retryVersionPost} disabled={versionBusy}>
          {versionBusy ? 'Retrying...' : 'Retry'}
        </Button>
      }
    >
      {versionPostError}
    </Alert>
  )

  // All three shown in every state below. A landed-but-unrecorded transaction
  // must stay retryable whichever branch the block has since moved to — and
  // recording a container anchor is precisely what moves it, so a banner
  // rendered only by the branch that raised it would disappear at the moment
  // it is needed.
  const pendingBanners = (
    <>
      {pendingPostBanner}
      {containerPendingBanner}
      {versionPendingBanner}
    </>
  )

  // Addendum tracks (T9). Rendered only once the video is anchored and
  // matches — the two branches below (6: ready to seal, 7: sealed) — since
  // an addendum can only exist once the store's own StartAddendum guard has
  // accepted the video as sealed in effect, which requires an anchor to
  // already be there. A video can be "sealed in effect" by window expiry
  // without ever having had seal_video called, so this is not folded into
  // branch 7 alone: chainStateOf only reports the on-chain enum variant, not
  // expiry, and addenda can exist under either.
  const addenda: AddendumTrack[] = info.addenda ?? []
  const addendaSection = addenda.length > 0 && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Addenda
      </Typography>
      {addenda.map((track) => (
        <Box key={track.lang} sx={{ mb: 1.5 }}>
          {track.chain ? (
            <Alert severity="success" sx={{ mb: 0.5 }}>
              <code>{track.lang}</code> ({track.cue_count} cues) — anchored on chain{' '}
              {formatIsoDate(track.chain.added_at)}.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <code>{track.lang}</code> — {track.cue_count} cues, built but not yet anchored on chain.
              </Typography>
              {addendaPendingSignature[track.lang] && (
                <Alert
                  severity="error"
                  sx={{ mb: 0.5 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => retryAddendumPost(track.lang)}
                      disabled={addendaBusy[track.lang]}
                    >
                      {addendaBusy[track.lang] ? 'Retrying...' : 'Retry'}
                    </Button>
                  }
                >
                  {addendaPostError[track.lang]}
                </Alert>
              )}
              {addendaActionError[track.lang] && (
                <Alert severity="error" sx={{ mb: 0.5 }}>
                  {addendaActionError[track.lang]}
                </Alert>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleAnchorAddendum(track)}
                disabled={addendaBusy[track.lang] || !program}
              >
                {addendaBusy[track.lang] ? 'Anchoring...' : 'Anchor on chain'}
              </Button>
            </>
          )}
        </Box>
      ))}
    </Box>
  )

  // Storage-location offer (T10). Legal at any chain state, sealed or not
  // (§6.6), so — like addendaSection — it is offered in both branch 6
  // (ready to seal) and branch 7 (sealed) below. Unlike addendaSection this
  // is never a list: only the current re-verified host is ever on offer,
  // and it disappears the instant that exact location shows up in
  // info.storage_anchors, matching PublicationSection's own
  // hostVerifiedNotRecorded gate — kept independent here (not passed as a
  // prop) since ChainAnchorBlock already has everything it needs off
  // video.publication and must not trust a value React captured earlier.
  const hostVerifiedUrl = info.host_verified_url
  const storageAlreadyRecorded =
    !!hostVerifiedUrl && (info.storage_anchors ?? []).some((a) => a.location === hostVerifiedUrl)
  const storageSection = hostVerifiedUrl && !storageAlreadyRecorded && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Storage location
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Re-verify confirmed <code>{hostVerifiedUrl}</code> serves the exact bytes the manifest
        committed to. Recording it here adds an on-chain note of where a copy can be found — it
        never changes what the video is, which is exactly why it is allowed even on a sealed
        video.
      </Typography>
      {storagePendingSignature !== null && (
        <Alert
          severity="error"
          sx={{ mb: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={retryStoragePost} disabled={storageBusy}>
              {storageBusy ? 'Retrying...' : 'Retry'}
            </Button>
          }
        >
          {storagePostError}
        </Alert>
      )}
      {storageActionError && (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {storageActionError}
        </Alert>
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={handleRecordStorage}
        disabled={storageBusy || !program}
      >
        {storageBusy ? 'Recording...' : 'Record storage location on chain'}
      </Button>
    </Box>
  )

  // 2. No wallet connected.
  if (!address) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect a wallet to anchor this video's root hash on-chain — an independently-checkable
          record that does not depend on this server's own database. Program{' '}
          <code>{FORUM_PROGRAM_ID.toBase58()}</code> on <ClusterBadge />.
        </Typography>
        {pendingBanners}
        {actionErrorBanner}
        <ConnectWallet />
      </Box>
    )
  }

  // Waiting on the two account reads that decide which action to offer.
  //
  // Keyed on publisherChecked alone, not on publisherLoading: the fetch is
  // started by an effect, so there is a render between "address arrived" and
  // "loading is true" where both flags are false. Treating that gap as loaded
  // flashes the "Create publisher identity" button at someone who already has
  // one, and it is clickable in that moment.
  //
  // The container reads join it for the same reason: until both the API's
  // container and the chain's answer about it are in, this component cannot
  // tell whether the next thing to offer is "create the container" or "seal
  // permanently", and flashing the seal button at someone for one frame is
  // exactly the mistake the paragraph above is about. containerChainChecked is
  // deliberately not `!containerChainLoading`: there is a render between the
  // container id arriving and the read starting where nothing is loading and
  // nothing is known yet.
  const containerChainChecked =
    containerPda === null ||
    (!containerChainLoading && (chainContainer !== null || containerChainMissing || containerChainError !== null))
  const stillLoading =
    videoLoading || (publisherPda !== null && !publisherChecked) || containerPending || !containerChainChecked
  if (stillLoading) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Reading chain state...
          </Typography>
        </Box>
      </Box>
    )
  }

  // 3. Publisher account missing — never created for this wallet.
  if (publisherChecked && publisher === null) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This wallet has no publisher identity on {solanaCluster} yet. Create one first — it is a
          one-time account that every video you anchor is published under.
        </Typography>
        {pendingBanners}
        {actionErrorBanner}
        <Button variant="outlined" onClick={handleCreatePublisher} disabled={busy || !program}>
          {busy ? 'Creating...' : 'Create publisher identity'}
        </Button>
      </Box>
    )
  }

  // 3a. Publisher exists, container account missing — the version list has
  // never been created on chain. Offered before every video-level state
  // below, and before the version branch that follows it, because
  // append_version needs this account to exist. Creating it needs nothing
  // else: init_container takes only the publisher and the container id, so
  // this is legal whether or not the video itself is anchored yet.
  //
  // Gated on the chain's answer, not on container.chain, so a container that
  // exists on chain is never offered a second create that could only fail at
  // init.
  if (container && containerChainMissing) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This video's container — the append-only list of every version published under it — does
          not exist on {solanaCluster} yet. It is a one-time account, and it is what lets a viewer
          read the version list off the chain rather than taking this server's word for which
          version is current.
        </Typography>
        {pendingBanners}
        {containerActionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {containerActionError}
          </Alert>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleInitContainer}
          disabled={containerBusy || !program}
        >
          {containerBusy ? 'Creating...' : 'Create container on chain'}
        </Button>
      </Box>
    )
  }

  // 3b. Container exists on chain, this version is not in its list yet.
  //
  // Only ever offered for the slot the chain is actually waiting for —
  // version_count is the seed of the next VersionRef, so appending out of
  // order would file this video at an index that is not its own and break the
  // correspondence between the two lists for good. When an earlier version is
  // the one missing, this falls through silently and VersionsSection above is
  // where the publisher sees which one it is.
  //
  // Also requires the Video account to exist (chainVideo): append_version
  // names it, so offering this first would only produce a failing
  // transaction. That is what puts branch 4's "Anchor on chain" ahead of this
  // in practice, matching the order the design's data flow lays out.
  if (
    container &&
    containerEntry &&
    chainContainer &&
    chainVideo &&
    !versionOnChain &&
    chainContainer.versionCount === containerEntry.index
  ) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This version's root hash is anchored, but it is not in the container's on-chain version
          list yet. Appending it is what makes it the current version as far as anyone reading the
          chain is concerned — until then a viewer is told the version they are watching is not the
          latest. Nothing already in the list changes: the list is append-only.
        </Typography>
        {pendingBanners}
        {versionActionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {versionActionError}
          </Alert>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAppendVersion}
          disabled={versionBusy || !program}
        >
          {versionBusy ? 'Appending...' : `Append version ${containerEntry.index + 1}`}
        </Button>
      </Box>
    )
  }

  // 7. Sealed on chain — checked ahead of the hash-mismatch case (5) on
  // purpose. Once sealed, the program refuses every write that could change
  // root_hash, so a mismatch here can only mean this UI is looking at a
  // stale local publication; offering "Correct on chain" would just be a
  // button that always fails. Show the sealed state and stop.
  if (chainVideo && chainStateOf(chainVideo) === ChainStateSealed) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        {pendingBanners}
        <Alert severity="success" sx={{ mb: 1 }}>
          Sealed on chain {formatChainDate(chainVideo.sealedAt.toNumber())}. root_hash can never
          change again — not by this publisher, not by us, not by anyone.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          revision {chainVideo.revision} ·{' '}
          {videoAddress && explorerHref ? (
            <Link href={explorerHref} target="_blank" rel="noopener noreferrer">
              {videoAddress}
            </Link>
          ) : (
            videoAddress
          )}
        </Typography>
        {addendaSection}
        {storageSection}
      </Box>
    )
  }

  // 4. Publisher exists, video account missing — first anchor.
  if (videoMissing) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This video's root hash has never been anchored on {solanaCluster}. Anchoring creates the
          on-chain record and starts the same correction window as the local publication.
        </Typography>
        {pendingBanners}
        {actionErrorBanner}
        <Button variant="contained" color="secondary" onClick={handlePublish} disabled={busy || !program}>
          {busy ? 'Anchoring...' : 'Anchor on chain'}
        </Button>
      </Box>
    )
  }

  if (!chainVideo) return null // exhausted every other case; nothing decidable yet

  const chainRootHash = toHex(chainVideo.rootHash)
  const hashesMatch = chainRootHash.toLowerCase() === info.root_hash.toLowerCase()

  // 5. On chain, but the root hash does not match the current publication.
  if (!hashesMatch) {
    return (
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Anchor on Solana
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The on-chain root hash (revision {chainVideo.revision}) does not match the current
          manifest. This is the republish-after-rebuild path — correcting it updates the on-chain
          record to match and resets the correction window.
        </Alert>
        {pendingBanners}
        {actionErrorBanner}
        <Button variant="contained" color="secondary" onClick={handleRepublish} disabled={busy || !program}>
          {busy ? 'Correcting...' : 'Correct on chain'}
        </Button>
      </Box>
    )
  }

  // 6. On chain, hashes match, still Published — ready to seal.
  return (
    <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Anchor on Solana
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Anchored on {solanaCluster}, revision {chainVideo.revision}, published{' '}
        {formatChainDate(chainVideo.publishedAt.toNumber())} —{' '}
        {videoAddress && explorerHref ? (
          <Link href={explorerHref} target="_blank" rel="noopener noreferrer">
            {videoAddress}
          </Link>
        ) : (
          videoAddress
        )}
        . Sealing on chain is permanent: there is no unseal, ever.
      </Typography>
      {pendingBanners}
      {actionErrorBanner}
      <Button variant="outlined" color="error" onClick={() => setConfirmSealOpen(true)} disabled={busy}>
        {busy ? 'Sealing...' : 'Seal on chain'}
      </Button>
      {addendaSection}
      {storageSection}

      <Dialog open={confirmSealOpen} onClose={() => setConfirmSealOpen(false)}>
        <DialogTitle>Seal this video on chain?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sealing on chain sets this video's on-chain state to Sealed. From that moment,
            root_hash can never change again — not by this publisher's wallet, not by us, not by
            anyone, and not by any instruction this program has. <strong>This cannot be undone —
            there is no unseal.</strong> Where the video is hosted can still change afterwards; the
            root hash is what a viewer checks the served bytes against.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSealOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmSealOpen(false)
              handleSeal()
            }}
          >
            Seal permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

interface ChainAnchorBlockProps {
  video: Video
}

/**
 * Mounts its own SolanaProvider so every page that never touches the chain —
 * every page except this one — pays nothing for the wallet adapters. Split
 * into a provider shell and an inner component that calls the hooks: a
 * provider cannot be consumed by the component that renders it.
 *
 * Skips mounting the provider at all when there is no manifest to anchor —
 * state 1 of the design is "render nothing", and that should not cost a
 * wallet-adapter mount either.
 */
const ChainAnchorBlock: FC<ChainAnchorBlockProps> = ({ video }) => {
  if (!video.publication) return null

  return (
    <SolanaProvider cluster={solanaCluster} endpoint={clusterRpcUrl()} wsEndpoint={clusterWsUrl()}>
      <ChainAnchorInner video={video} />
    </SolanaProvider>
  )
}

export default ChainAnchorBlock
